import PropTypes from 'prop-types'

import { notifyParent, STORE_KEY_ACCOUNT, storeGet } from 'mailup-react-helpers'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil'

import styled from '@emotion/styled'
import {
  Button,
  BUTTON_VARIANT_SECONDARY,
  ContextMenu,
  Flex,
  IconArrow1Down,
  makeButtonWithMenu,
  Spinner,
  Thumb,
  ThumbContainer,
  ThumbContainerFooter,
  ThumbDefaultError,
  ThumbDefaultOverlay
} from 'mailup-web-components'

import {
  changeWizardStepCallback,
  fetchStep4ThumbnailCallback
} from 'Pages/Campaign/recoil/callbacks/callbacks'
import { campaignState } from 'Pages/Campaign/recoil/state/campaign'
import {
  messageIsOnSentState,
  pageState,
  thumbnailState
} from 'Pages/Campaign/recoil/state/page'

const Thumbnail = () => {
  const [imgSrc, setImgSrc] = useState('')
  const { state, url } = useRecoilValue(thumbnailState)
  const {
    EmailOptions: { IdMessage }
  } = useRecoilValue(campaignState)

  const fetchThumbnail = useRecoilCallback(fetchStep4ThumbnailCallback, [])

  const onClickPreview = () => {
    state !== 'error' && notifyParent('openPreviewMainFrame', [IdMessage])
  }

  const getImage = () => {
    fetchThumbnail()
  }

  useEffect(() => {
    const { Domain } = storeGet(STORE_KEY_ACCOUNT)
    setImgSrc(`//${Domain}/${url}`)
  }, [url])

  return (
    <>
      <StyledSpan>
        <ThumbContainer>
          <Thumb
            aspectRatio={278 / 323}
            HoverComponent={state !== 'error' ? Overlay : undefined}
            onClick={onClickPreview}
          >
            {state === 'loaded' && <img src={imgSrc} />}
            {state === 'loading' && <Spinner />}
            {state === 'error' && <ContentError onClick={getImage} />}
          </Thumb>
          <ThumbContainerFooter>
            <StyledFlex padding='16px 32px' justifyContent='center'>
              <ButtonWithMenuInFooter />
            </StyledFlex>
          </ThumbContainerFooter>
        </ThumbContainer>
      </StyledSpan>
    </>
  )
}

export default Thumbnail

const StyledFlex = styled(Flex)`
  > button {
    width: 100% !important;
    > span {
      width: 100%;
      justify-content: space-between;
      > div:nth-child(1) {
        margin: auto;
      }
    }
  }
`

const StyledSpan = styled.span`
  position: relative;

  > div {
    overflow: visible;

    .e-thumb {
      border-radius: 6px;
      > div,
      > img {
        border-radius: 6px 6px 0 0;
      }
    }
  }
`

const StyledIconDown = styled(IconArrow1Down)`
  width: 13px;
  height: 13px;
  color: ${({ disabled }) =>
    disabled ? 'var(--silver-chalice)' : 'var(--java)'};
`

const StyledButtonGroup = styled.div`
  position: relative;
  display: flex;
  width: 100%;

  > button {
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
    flex: 1 0 auto;
  }

  .c-button-with-menu__button {
    position: static;
    margin-right: 0 !important;

    > button {
      border-top-left-radius: 0 !important;
      border-bottom-left-radius: 0 !important;
      padding: 0 13px;
      border-left: none !important;
    }
  }

  .c-button-with-menu__menu {
    position: absolute;
    right: -8px;
  }

  .e-context-menu {
    width: 220px;
  }
`

const ButtonWithMenuInFooter = () => {
  const intl = useIntl()

  const {
    EmailOptions: { IdMessage }
  } = useRecoilValue(campaignState)
  const { isOnSent } = useRecoilValue(messageIsOnSentState)
  const setPageState = useSetRecoilState(pageState)
  const changeWizardStep = useRecoilCallback(changeWizardStepCallback, [])

  const onClickChangeModel = async () => {
    setPageState((prev) => ({
      ...prev,
      forceTemplates: true
    }))
    changeWizardStep(2)
  }

  const onClickOverview = () => {
    notifyParent('redirectMainFrame', [
      `/console/messages_app_redirector.aspx?r=/messages/email/${IdMessage}/overview`
    ])
  }

  const onClickEdit = async () => {
    setPageState((prev) => ({
      ...prev,
      forceTemplates: false
    }))
    changeWizardStep(2)
  }

  const ButtonComponent = () => (
    <Button
      kind={BUTTON_VARIANT_SECONDARY}
      disabled={isOnSent}
      data-mu-test='campaign-wizard__step-4__edit-msg-button'
    >
      <StyledIconDown disbaled={isOnSent} />
    </Button>
  )

  const MenuComponent = () => {
    const items = [
      {
        label: intl.formatMessage({
          id: 'Load.MailupCommon.Edit'
        }),
        value: 'EDIT',
        onClick: onClickEdit,
        'data-mu-test': 'action--edit'
      },
      {
        label: intl.formatMessage({
          id: 'Load.ContentsCampaignWizard.ChangeTemplate'
        }),
        value: 'CHANGE_MODEL',
        onClick: onClickChangeModel,
        'data-mu-test': 'action--change-model'
      },
      {
        label: intl.formatMessage({
          id: 'Load.EmailMessageList.ActionMessageOverview'
        }),
        value: 'OVERVIEW',
        onClick: onClickOverview,
        'data-mu-test': 'action--overview'
      }
    ]

    return (
      <ContextMenu
        items={items}
        data-mu-test='campaign__step-4__actions-menu'
      />
    )
  }

  const ButtonWithMenu = makeButtonWithMenu(ButtonComponent, MenuComponent)

  return (
    <StyledButtonGroup>
      <Button
        kind={BUTTON_VARIANT_SECONDARY}
        onClick={onClickEdit}
        disabled={isOnSent}
      >
        {intl.formatMessage({ id: 'Load.MailupCommon.Edit' })}
      </Button>
      <ButtonWithMenu />
    </StyledButtonGroup>
  )
}

const ContentError = ({ onClick }) => {
  const intl = useIntl()
  return (
    <ThumbDefaultError onClick={onClick}>
      {intl.formatMessage({ id: 'Load.MailupCommon.PreviewRefresh' })}
    </ThumbDefaultError>
  )
}

const Overlay = () => {
  const intl = useIntl()

  return (
    <ThumbDefaultOverlay>
      {intl.formatMessage({ id: 'Load.MailupCommon.PreviewOpen' })}
    </ThumbDefaultOverlay>
  )
}

ContentError.propTypes = {
  onClick: PropTypes.func.isRequired
}
